/**
 * What each plan allows, and what the paid plan costs. Toby's decision
 * (25 October 2026, replacing 23 September's 3 bikes free and £20 a year):
 * the first bike is free; Klunkers Plus is £4.99 a year for any number of
 * bikes and includes the AI features, which Free doesn't have. The storage
 * figures are still provisional (see docs/subscriptions.md). Change these
 * here and nowhere else.
 *
 * Nothing Strava-related may ever sit behind the paid plan (Strava API
 * Policy 5.8), so no limit here may depend on Strava data.
 */
import { z } from 'zod'
import { Plan } from './enums'
import { formatMoney } from './money'

export interface PlanLimits {
  /** Bikes in the garage that are not sold or retired. null = unlimited. */
  bikes: number | null
  /** Photos and voice notes, in bytes. null = unlimited. */
  storage_bytes: number | null
  /** Phase 4 AI features (component ID, diagnosis). */
  ai: boolean
}

const GB = 1024 ** 3

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: { bikes: 1, storage_bytes: 1 * GB, ai: false },
  paid: { bikes: null, storage_bytes: 50 * GB, ai: true },
}

export const planLabel: Record<Plan, string> = { free: 'Free', paid: 'Klunkers Plus' }

/** What the paid plan costs. Charged through the app stores or Stripe once payments are built. */
export const PAID_PLAN_PRICE = { amount_minor: 499, currency: 'GBP', interval: 'year' } as const

/** The paid plan's price for display, e.g. "£4.99". */
export const paidPlanPrice = formatMoney(PAID_PLAN_PRICE.amount_minor, PAID_PLAN_PRICE.currency)

export const PlanStatus = z.object({
  plan: Plan,
  limits: z.object({
    bikes: z.number().nullable(),
    storage_bytes: z.number().nullable(),
    ai: z.boolean(),
  }),
  usage: z.object({ bikes: z.number().int(), storage_bytes: z.number() }),
  /** Whether limits are enforced yet. Off until pricing is decided. */
  enforced: z.boolean(),
})
export type PlanStatus = z.infer<typeof PlanStatus>

export type LimitCheck = { ok: true } | { ok: false; message: string }

/** May the rider have one more active bike? */
export function canAddBike(plan: Plan, activeBikes: number): LimitCheck {
  const max = PLAN_LIMITS[plan].bikes
  if (max === null || activeBikes < max) return { ok: true }
  return {
    ok: false,
    message: `The ${planLabel[plan]} plan has room for ${max === 1 ? 'one bike' : `${max} bikes`}. Mark one as sold or retired, or upgrade to ${planLabel.paid} (${paidPlanPrice} a year) for unlimited bikes.`,
  }
}

/** May the rider store `adding` more bytes? */
export function canStore(plan: Plan, usedBytes: number, adding: number): LimitCheck {
  const max = PLAN_LIMITS[plan].storage_bytes
  if (max === null || usedBytes + adding <= max) return { ok: true }
  const gb = (max / GB).toFixed(0)
  return {
    ok: false,
    message: `That would go over the ${gb} GB of photo storage on the ${planLabel[plan]} plan. Delete some photos, or upgrade for more space.`,
  }
}
